
/**
 * // raw validation rule set
 * @typedef {Object} ValidationRuleInternational
 * @property {string} pattern - The validation pattern to test against
 * @property {string} [en] - The message to show if validation fails in English
 * @property {string} [sv] - The message to show if validation fails in Swedish
 * @property {string} [de] - The message to show if validation fails in German
 * @property {string} [da] - The message to show if validation fails in Danish
 * @property {string} [fi] - The message to show if validation fails in Finnish
 * @property {string} [fr] - The message to show if validation fails in French
 * @property {string} [nl] - The message to show if validation fails in Dutch
 */

/**
 * @typedef {Object} ValidationRule
 * @property {string} pattern
 * @property {string} invalidMessage
 */


/**
 * @typedef {string} ParsingError
 */



export const rules = {
    /**
     * @type {ValidationRuleInternational[]}
     * Collection of validation rule sets for different field types
     */
    companyName: [
        {
            pattern: '^.{1,60}$',
            en: 'Company name must be less than 60 characters',
            sv: 'Företagsnamnet får inte vara längre än 60 tecken',
            de: 'Firmenname darf nicht länger als 60 Zeichen sein',
            da: 'Virksomhedsnavn må ikke være længere end 60 tegn',
            fi: 'Yrityksen nimi ei saa olla pidempi kuin 60 merkkiä',
            fr: 'Le nom de l\'entreprise ne doit pas dépasser 60 caractères',
            nl: 'Bedrijfsnaam mag niet langer zijn dan 60 tekens'
        },
        {
            pattern: '^[^<>={}]+$',
            en: 'Invalid characters. (not allowed: <>={})',
            sv: 'Ogiltiga tecken. (ej tillåtna: <>={})',
            de: 'Ungültige Zeichen enthalten. (nicht erlaubt: <>={})',
            da: 'Ugyldige tegn. (ikke tilladt: <>={})',
            fi: 'Virheellisiä merkkejä. (ei sallittu: <>={})',
            fr: 'Caractères invalides. (non autorisés: <>={})',
            nl: 'Ongeldige tekens. (niet toegestaan: <>={})',
        }
    ],
    /**
     * @type {ValidationRuleInternational[]}
     * Collection of validation rule sets for different field types
     */
    orgNumber: [
        {
            pattern: '^[a-zA-Z0-9\\-]{6,32}$',
            en: "Incorrect format",
            sv: "Ej korrekt format",
            de: "Falsches Format",
            da: "Forkert format",
            fi: "Väärä muoto",
            fr: "Format incorrect",
            nl: "Onjuist formaat",
        }
    ],
    /**
     * @type {ValidationRuleInternational[]}
     * Collection of validation rule sets for different field types
     */
    name: [
        {
            pattern: '^[^0-9]+$',
            en: 'Name cannot contain numbers',
            sv: 'Namnet får inte innehålla siffror',
            de: 'Name darf keine Zahlen enthalten',
            da: 'Navn må ikke indeholde tal',
            fi: 'Nimi ei voi sisältää numeroita',
            fr: 'Le nom ne peut pas contenir de chiffres',
            nl: 'Naam mag geen cijfers bevatten'
        },
        {
            pattern: '^[^0-9!<>,;?=+()@#"�{}_$%:]+$',
            en: "Invalid characters. (not allowed: !<>,;?=+()@#\"_$%:)",
            sv: "Ogiltiga tecken. (ej tillåtna: !<>,;?=+()@#\"_$%:)",
            de: "Ungültige Zeichen enthalten. (nicht erlaubt: !<>,;?=+()@#\"_$%:)",
            da: "Ugyldige tegn. (ikke tilladt: !<>,;?=+()@#\"_$%:)",
            fi: "Virheellisiä merkkejä. (ei sallittu: !<>,;?=+()@#\"_$%:)",
            fr: "Caractères invalides. (non autorisés: !<>,;?=+()@#\"_$%:)",
            nl: "Ongeldige tekens. (niet toegestaan: !<>,;?=+()@#\"_$%:)",
        }
    ],
    /**
     * @type {ValidationRuleInternational[]}
     * Collection of validation rule sets for different field types
     */
    email: [
        {
            pattern: '@',
            en: 'E-mail address must contain an @',
            sv: 'E-postadressen måste innehålla ett @',
            de: 'E-Mail-Adresse muss ein @ enthalten',
            da: 'E-Mail-adresse skal indeholde et @',
            fi: 'Sähköpostiosoite sisältää @ merkinnän',
            fr: 'L\'adresse e-mail doit contenir un @',
            nl: 'E-mailadres moet een @ bevatten'
        },
        {
            pattern: '^[\\S ]{1,64}@',
            en: 'The first part of the e-mail address must be between 1 and 64 characters',
            sv: 'Den första delen av e-postadressen måste vara mellan 1 och 64 tecken',
            de: 'Der erste Teil der E-Mail-Adresse muss zwischen 1 und 64 Zeichen lang sein',
            da: 'Den første del af e-mailadressen skal være mellem 1 og 64 tegn',
            fi: 'Sähköpostiosoitteen ensimmäisen osan tulee olla 1-64 merkkiä',
            fr: 'La première partie de l\'adresse e-mail doit comporter entre 1 et 64 caractères',
            nl: 'Het eerste deel van het e-mailadres moet tussen 1 en 64 tekens lang zijn'
        },
        {
            pattern: '[^@]$',
            en: 'Can not end with @',
            sv: 'Får inte sluta med @',
            de: 'Darf nicht mit @ enden',
            da: 'Må ikke slutte med @',
            fi: 'Ei voi päättyä @-merkkiin',
            fr: 'Ne peut pas se terminer par @',
            nl: 'Mag niet eindigen met @'
        },
        {
            pattern: '^(?:[^@]|"[^"]*")*@(?:[^@]|"[^"]*")*$',
            en: 'Email address cannot contain more than one @',
            sv: 'E-postadressen får inte innehålla mer än ett @',
            de: 'E-Mail-Adresse darf nicht mehr als ein @ enthalten',
            da: 'E-mailadresse må ikke indeholde mere end ét @',
            fi: 'Sähköpostiosoite ei saa sisältää enempää kuin yhden @-merkin',
            fr: 'L\'adresse e-mail ne peut pas contenir plus d\'un @',
            nl: 'E-mailadres mag niet meer dan één @ bevatten'
        },
        {
            pattern: '@[^_\\n]+$',
            en: 'Underscore is not allowed in the domain part',
            sv: 'Understreck är inte tillåtet i domändelen',
            de: 'Unterstrich ist im Domain-Teil nicht erlaubt',
            da: 'Understregning er ikke tilladt i domænedelen',
            fi: 'Alaviiva ei ole sallittu verkkotunnusosassa',
            fr: 'Le trait de soulignement n\'est pas autorisé dans la partie domaine',
            nl: 'Underscore is niet toegestaan in het domeingedeelte'
        },
        {
            pattern: '@[^\\s]+$',
            en: 'The domain part of the email address cannot contain spaces',
            sv: 'Domändelen av e-postadressen får inte innehålla mellanslag',
            de: 'Der Domain-Teil der E-Mail-Adresse darf keine Leerzeichen enthalten',
            da: 'Domænedelen af e-mailadressen må ikke indeholde mellemrum',
            fi: 'Sähköpostiosoitteen verkkotunnusosassa ei saa olla välilyöntejä',
            fr: 'La partie domaine de l\'adresse e-mail ne doit pas contenir d\'espaces',
            nl: 'Het domeingedeelte van het e-mailadres mag geen spaties bevatten'
        },
        {
            pattern: '\\.[^.@\\[\\]]{2,64}$',
            en: 'Email address must contain a valid top-level domain',
            sv: 'E-postadressen måste innehålla en giltig toppdomän',
            de: 'Die E-Mail-Adresse muss eine gültige Top-Level-Domain enthalten',
            da: 'E-mailadressen skal indeholde et gyldigt topdomæne',
            fi: 'Sähköpostiosoitteessa on oltava kelvollinen ylätason verkkotunnus',
            fr: 'L\'adresse e-mail doit contenir un domaine de premier niveau valide',
            nl: 'Het e-mailadres moet een geldig topleveldomein bevatten'
        },
        {
            pattern: '@(?:[a-zA-Z0-9\\-\\.]+(?:.[a-zA-Z0-9-]+)*)$|(?:\\[IPv6(?::[0-9a-f]{1,4}){8}\\])|(?:\\[\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}\\.\\d{1,3}\\])$',
            en: 'The domain part of the email address contains invalid characters',
            sv: 'Domändelen av e-postadressen innehåller ogiltiga tecken',
            de: 'Der Domain-Teil der E-Mail-Adresse enthält ungültige Zeichen',
            da: 'Domænedelen af e-mailadressen indeholder ugyldige tegn',
            fi: 'Sähköpostiosoitteen verkkotunnusosa sisältää virheellisiä merkkejä',
            fr: 'La partie domaine de l\'adresse e-mail contient des caractères non valides',
            nl: 'Het domeingedeelte van het e-mailadres bevat ongeldige tekens'
        },
        {
            pattern: '^[a-zA-Z0-9!#$%&\'*+/=?^_`{|}~]+|(?:"[a-zA-Z0-9 "(),?!:;<>@\\[\\\\\\]#$%&\'*+-/=^_`{|}~]+")',
            en: 'Invalid character in first part of email address',
            sv: 'Ogiltigt tecken i första delen av e-postadressen',
            de: 'Ungültiges Zeichen im ersten Teil der E-Mail-Adresse',
            da: 'Ugyldigt tegn i første del af e-mailadressen',
            fi: 'Virheellinen merkki sähköpostiosoitteen ensimmäisessä osassa',
            fr: 'Caractère invalide dans la première partie de l\'adresse e-mail',
            nl: 'Ongeldig teken in het eerste deel van het e-mailadres'
        },
        {
            pattern: '(?:^".+"@)|(?:"[^"]+"[\\.@])|^[^"]+$',
            en: 'Quoted parts must be separated by dots or make up the entire local part',
            sv: 'Citerade delar måste separeras med punkter eller utgöra hela den lokala delen',
            de: 'Zitierte Teile müssen durch Punkte getrennt sein oder den gesamten lokalen Teil ausmachen',
            da: 'Citerede dele skal adskilles af punktummer eller udgøre hele den lokale del',
            fi: 'Lainausmerkeillä merkittyjen osien tulee olla erotettu pisteillä tai muodostaa koko paikallinen osa',
            fr: 'Les parties entre guillemets doivent être séparées par des points ou constituer l\'intégralité de la partie locale',
            nl: 'Geciteerde delen moeten gescheiden zijn door punten of het hele lokale deel vormen'
        },
    ],
    /**
     * @type {ValidationRuleInternational[]}
     * Collection of validation rule sets for different field types
     */
    phone: [
        {
            pattern: '^[+(0-9]',
            en: 'Phone number must start with a digit, + or (',
            sv: 'Telefonnumret måste börja en siffra, + eller (',
            de: 'Telefonnummer muss mit einer Ziffer, + oder ( beginnen',
            da: 'Telefonnummeret skal starte med et tal, + eller (',
            fi: 'Puhelinnumeron tulee alkaa numerolla, + tai (',
            fr: 'Le numéro de téléphone doit commencer par un chiffre, + ou (',
            nl: 'Telefoonnummer moet beginnen met een cijfer, + of ('
        },
        {
            pattern: '^[0-9+\\- \\(\\)]*$',
            en: 'Phone number may only contain digits, +, -, (), and spaces',
            sv: 'Telefonnumret får bara innehålla siffror, +, -, (), och mellanslag',
            de: 'Telefonnummer darf nur Ziffern, +, -, (), und Leerzeichen enthalten',
            da: 'Telefonnummeret må kun indeholde tal, +, -, (), og mellemrum',
            fi: 'Puhelinnumero saa sisältää vain numeroita, +, -, (), ja välilyöntejä',
            fr: 'Le numéro de téléphone ne peut contenir que des chiffres, +, -, (), et des espaces',
            nl: 'Telefoonnummer mag alleen cijfers, +, -, (), en spaties bevatten'
        },
        {
            pattern: '^(?:[^0-9]*[0-9]){6,26}[^0-9]*$',
            en: 'Phone number must contain between 6 and 26 digits',
            sv: 'Telefonnumret måste innehålla mellan 6 och 26 siffror',
            de: 'Telefonnummer muss zwischen 6 und 26 Ziffern enthalten',
            da: 'Telefonnummeret skal indeholde mellem 6 og 26 cifre',
            fi: 'Puhelinnumerossa on oltava 6-26 numeroa',
            fr: 'Le numéro de téléphone doit contenir entre 6 et 26 chiffres',
            nl: 'Telefoonnummer moet tussen 6 en 26 cijfers bevatten'
        }
    ],
    /**
     * @type {ValidationRuleInternational[]}
     * Collection of validation rule sets for different field types
     */
    numberPositive: [
        {
            pattern: '^[0-9 ]*$',
            en: 'The field can only contain numbers',
            sv: 'Fältet kan bara innehålla siffror',
            de: 'Das Feld darf nur Zahlen enthalten',
            da: 'Feltet kan kun indeholde tal',
            fi: 'Kenttä voi sisältää vain numeroita',
            fr: 'Le champ ne peut contenir que des chiffres',
            nl: 'Het veld kan alleen cijfers bevatten'
        }
    ],
    /**
     * @type {ValidationRuleInternational[]}
     * Collection of validation rule sets for different field types
     */
    textArea: [
        {
            pattern: '^.{0,500}$',
            en: 'Max 500 characters',
            sv: 'Max 500 tecken',
            de: 'Max 500 Zeichen',
            da: 'Max 500 tegn',
            fi: 'Max 500 merkkiä',
            fr: 'Max 500 caractères',
            nl: 'Max 500 tekens'
        }
    ],
    /**
    * @type {ValidationRuleInternational[]}
    * Collection of validation rule sets for different field types
    */
    nameFirst: [
        {
            pattern: '^[^0-9]+$',
            en: 'First name cannot contain numbers',
            sv: 'Förnamn får inte innehålla siffror',
            de: 'Vorname darf keine Zahlen enthalten',
            da: 'Fornavn må ikke indeholde tal',
            fi: 'Etunimi ei voi sisältää numeroita',
            fr: 'Le prénom ne peut pas contenir de chiffres',
            nl: 'Voornaam mag geen cijfers bevatten'
        },
        {
            pattern: '^[^0-9!<>,;?=+()@#"�{}_$%:]+$',
            en: "Invalid character (not allowed: <>,;?=+()@#\"_$%:)",
            sv: "Ogiltigt tecken (ej tillåtet: <>,;?=+()@#\"_$%:)",
            de: "Ungültiges Zeichen (nicht erlaubt: <>,;?=+()@#\"_$%:)",
            da: "Ugyldigt tegn (ikke tilladt: <>,;?=+()@#\"_$%:)",
            fi: "Virheellinen merkki (ei sallittu: <>,;?=+()@#\"_$%:)",
            fr: "Caractère invalide (non autorisé: <>,;?=+()@#\"_$%:)",
            nl: "Ongeldig teken (niet toegestaan: <>,;?=+()@#\"_$%:)",
        }
    ],
    /**
     * @type {ValidationRuleInternational[]}
     * Collection of validation rule sets for different field types
     */
    nameLast: [
        {
            pattern: '^[^0-9]+$',
            en: 'Last name cannot contain numbers',
            sv: 'Efternamn får inte innehålla siffror',
            de: 'Nachname darf keine Zahlen enthalten',
            da: 'Efternavn må ikke indeholde tal',
            fi: 'Sukunimi ei voi sisältää numeroita',
            fr: 'Le nom de famille ne peut pas contenir de chiffres',
            nl: 'Achternaam mag geen cijfers bevatten'
        },
        {
            pattern: '^[^0-9!<>,;?=+()@#"�{}_$%:]+$',
            en: "Invalid character (not allowed: !<>,;?=+()@#\"_$%:)",
            sv: "Ogiltigt tecken (ej tillåtet: !<>,;?=+()@#\"_$%:)",
            de: "Ungültiges Zeichen (nicht erlaubt: !<>,;?=+()@#\"_$%:)",
            da: "Ugyldigt tegn (ikke tilladt: !<>,;?=+()@#\"_$%:)",
            fi: "Virheellinen merkki (ei sallittu: !<>,;?=+()@#\"_$%:)",
            fr: "Caractère invalide (non autorisé: !<>,;?=+()@#\"_$%:)",
            nl: "Ongeldig teken (niet toegestaan: !<>,;?=+()@#\"_$%:)",
        }
    ],
    /**
     * @type {ValidationRuleInternational[]}
     * Collection of validation rule sets for different field types
     */
    checkoutMessage: [
        {
            pattern: '^(?:[\\S\\s]{1,300})$',
            en: 'Message must be less than 300 characters',
            sv: 'Meddelandet får inte vara längre än 300 tecken',
            de: 'Nachricht darf nicht länger als 300 Zeichen sein',
            da: 'Besked må ikke være længere end 300 tegn',
            fi: 'Viesti ei saa olla pidempi kuin 300 merkkiä',
            fr: 'Le message ne doit pas dépasser 300 caractères',
            nl: 'Bericht moet minder dan 300 tekens zijn'
        },
        {
            pattern: '^[^<>{}]+$',
            en: 'Invalid character (not allowed: <>{})',
            sv: 'Ogiltigt tecken (ej tillåtet: <>{})',
            de: 'Ungültiges Zeichen (nicht erlaubt: <>{})',
            da: 'Ugyldigt tegn (ikke tilladt: <>{})',
            fi: 'Virheellinen merkki (ei sallittu: <>{})',
            fr: 'Caractère invalide (non autorisé: <>{})',
            nl: 'Ongeldig teken (niet toegestaan: <>{})'
        }
    ],
    /**
     * @type {ValidationRuleInternational[]}
     * Collection of validation rule sets for different field types
     */
    restrictTags: [
        {
            pattern: '^[^<>{}]+$',
            en: 'Invalid character (not allowed: <>{})',
            sv: 'Ogiltigt tecken (ej tillåtet: <>{})',
            de: 'Ungültiges Zeichen (nicht erlaubt: <>{})',
            da: 'Ugyldigt tegn (ikke tilladt: <>{})',
            fi: 'Virheellinen merkki (ei sallittu: <>{})',
            fr: 'Caractère invalide (non autorisé: <>{})',
            nl: 'Ongeldig teken (niet toegestaan: <>{})'
        }
    ],
    /**
     * @type {ValidationRuleInternational[]}
     * Collection of validation rule sets for different field types
     */
    required: [
        {
            pattern: '\\S',
            en: 'This field is required',
            sv: 'Detta fält är obligatoriskt',
            de: 'Dieses Feld ist erforderlich',
            da: 'Dette felt er påkrævet',
            fi: 'Tämä kenttä on pakollinen',
            fr: 'Ce champ est obligatoire',
            nl: 'Dit veld is verplicht'
        }
    ]
}